import pdfMake from 'pdfmake/build/pdfmake'
import * as pdfFonts from 'pdfmake/build/vfs_fonts'
import type { Column, Content, TDocumentDefinitions } from 'pdfmake/interfaces'
import type { OrderPdfCoverSheet, OrderPdfData, OrderPdfLetter, OrderPdfSection } from './pdfTypes'
import { urlToDataURL } from './pdfHelpers'
import { logError } from '../shared/errorUtils'
import {
  BRAND_BLUE,
  HEADER_BG,
  BORDER_COLOR,
  TEXT_SECONDARY,
  TABLE_LINE_COLOR,
  SUPERVISING_PHYSICIAN_NAME,
  SUPERVISING_PHYSICIAN_NPI,
} from './constants'
import logo from '/logo.png'

// Initialize pdfmake VFS
const vfs =
  (pdfFonts as Record<string, unknown>).vfs ||
  (pdfFonts as Record<string, Record<string, unknown>>).pdfMake?.vfs
if (vfs) {
  pdfMake.vfs = vfs as Record<string, string>
}

function buildSectionTitle(title: string): Content {
  return {
    text: title.toUpperCase(),
    fontSize: 8,
    bold: true,
    color: BRAND_BLUE,
    margin: [0, 6, 0, 2],
  }
}

function buildKeyValueTable(data: Record<string, string>): Content {
  const entries = Object.entries(data).filter(([, v]) => v)
  if (entries.length === 0) return { text: '' }

  return {
    table: {
      widths: ['30%', '70%'],
      body: entries.map(([key, value]) => [
        { text: key, fontSize: 7, bold: true, color: TEXT_SECONDARY },
        { text: value, fontSize: 7 },
      ]),
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0,
      hLineColor: () => TABLE_LINE_COLOR,
      paddingTop: () => 1,
      paddingBottom: () => 1,
    },
    margin: [0, 0, 0, 2] as [number, number, number, number],
  }
}

function buildDataTable(headers: string[], rows: string[][]): Content {
  if (rows.length === 0) return { text: '' }

  return {
    table: {
      headerRows: 1,
      widths: headers.map(() => '*'),
      body: [
        headers.map((h) => ({
          text: h,
          fontSize: 6.5,
          bold: true,
          fillColor: HEADER_BG,
          color: BRAND_BLUE,
        })),
        ...rows.map((row) => row.map((cell) => ({ text: cell, fontSize: 6.5 }))),
      ],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => BORDER_COLOR,
      vLineColor: () => BORDER_COLOR,
      paddingTop: () => 2,
      paddingBottom: () => 2,
      paddingLeft: () => 3,
      paddingRight: () => 3,
    },
    margin: [0, 0, 0, 2] as [number, number, number, number],
  }
}

function buildList(items: string[]): Content {
  if (items.length === 0) return { text: '' }
  return {
    ul: items.map((item) => ({ text: item, fontSize: 7 })),
    margin: [0, 0, 0, 2] as [number, number, number, number],
  }
}

function renderVendorSection(section: OrderPdfSection): Content[] {
  const title = buildSectionTitle(section.title)

  switch (section.type) {
    case 'keyValue':
      return [title, buildKeyValueTable(section.data)]
    case 'table':
      return [title, buildDataTable(section.headers, section.rows)]
    case 'list':
      return [title, buildList(section.items)]
    case 'text':
      return [
        title,
        {
          text: section.content,
          fontSize: 7,
          margin: [0, 0, 0, 2] as [number, number, number, number],
        },
      ]
  }
}

/**
 * The patient rows a requisition prints, empty ones already dropped.
 *
 * PD-1529 — `Email` is included because a lab may require patient contact
 * details and a chart can hold an email but no phone; printing only the phone
 * would send such an order out with no way to reach the patient.
 */
/**
 * `includeEmail` is opt-in per vendor. The established requisitions omit the
 * patient's email deliberately, so only a vendor that actually asks for it
 * turns this on — GTI, whose stated acceptance criteria require patient contact
 * info and whose order may satisfy that with an email alone.
 */
export function buildPatientInfoRows(
  p: OrderPdfData['patient'],
  includeEmail = false,
): [string, string][] {
  const entries: [string, string][] = [
    ['Name', [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ')],
    ['Date of Birth', p.dateOfBirth],
    ['Sex', p.sex],
    ['Phone', p.phone],
    ...(includeEmail ? ([['Email', p.email]] as [string, string][]) : []),
    ['Address', [p.address, p.city, p.state, p.zip].filter(Boolean).join(', ')],
  ]

  return entries.filter(([, value]) => value)
}

function buildPatientInfoTable(data: OrderPdfData): Content {
  const entries = buildPatientInfoRows(data.patient, data.includePatientEmail)

  return {
    table: {
      widths: ['30%', '70%'],
      body: entries.map(([key, value]) => [
        { text: key, fontSize: 7, bold: true, color: TEXT_SECONDARY },
        { text: value, fontSize: 7 },
      ]),
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0,
      hLineColor: () => TABLE_LINE_COLOR,
      paddingTop: () => 1,
      paddingBottom: () => 1,
    },
  }
}

export function buildProviderHeaderStack(data: OrderPdfData): Content {
  const pr = data.provider
  const items: Content[] = []

  if (pr.organizationName) {
    items.push({
      text: pr.organizationName,
      fontSize: 9,
      bold: true,
      color: BRAND_BLUE,
      margin: [0, 0, 0, 2] as [number, number, number, number],
    })
  }
  if (pr.providerName) {
    const npiPart = pr.npi ? `  |  NPI: ${pr.npi}` : ''
    items.push({
      text: `${pr.providerName}${npiPart}`,
      fontSize: 7.5,
      color: '#333',
      margin: [0, 0, 0, 1] as [number, number, number, number],
    })
  }
  items.push({
    text: `Supervising Physician: ${SUPERVISING_PHYSICIAN_NAME}  |  NPI: ${SUPERVISING_PHYSICIAN_NPI}`,
    fontSize: 7.5,
    color: '#333',
    margin: [0, 0, 0, 1] as [number, number, number, number],
  })
  if (pr.accountNumber) {
    items.push({
      text: `Account #: ${pr.accountNumber}`,
      fontSize: 7,
      bold: true,
      color: '#555',
      margin: [0, 0, 0, 1] as [number, number, number, number],
    })
  }
  const contactParts: string[] = []
  // PD-1683 — the practice's phone goes on every requisition: a lab that cannot route a
  // result, or has a question about the order, otherwise has only a fax to reach us on.
  if (pr.phone) contactParts.push(`Phone: ${pr.phone}`)
  if (pr.fax) contactParts.push(`${data.providerFaxLabel ?? 'Fax'}: ${pr.fax}`)
  if (contactParts.length > 0) {
    items.push({
      text: contactParts.join('  |  '),
      fontSize: 7,
      bold: true,
      color: '#555',
      margin: [0, 0, 0, 1] as [number, number, number, number],
    })
  }
  const addr = [pr.address, pr.city, pr.state, pr.zip].filter(Boolean).join(', ')
  if (addr) {
    items.push({
      text: addr,
      fontSize: 7,
      color: '#555',
      margin: [0, 0, 0, 0] as [number, number, number, number],
    })
  }

  return { stack: items }
}

function buildFacilityHeaderStack(facility: NonNullable<OrderPdfData['facilityHeader']>): Content {
  const items: Content[] = []

  items.push({
    text: facility.name,
    fontSize: 9,
    bold: true,
    color: BRAND_BLUE,
    margin: [0, 0, 0, 2] as [number, number, number, number],
  })
  if (facility.subtitle) {
    items.push({
      text: facility.subtitle,
      fontSize: 7,
      color: '#555',
      margin: [0, 0, 0, 1] as [number, number, number, number],
    })
  }
  if (facility.address) {
    items.push({
      text: facility.address,
      fontSize: 7,
      color: '#555',
      margin: [0, 0, 0, 1] as [number, number, number, number],
    })
  }
  if (facility.fax && facility.fax !== 'N/A') {
    items.push({
      text: `Fax: ${facility.fax}`,
      fontSize: 7,
      bold: true,
      color: '#555',
      margin: [0, 0, 0, 0] as [number, number, number, number],
    })
  }

  return { stack: items }
}

/**
 * The letterhead block: logo + provider info, optionally with a facility on the right.
 *
 * PD-1651 — extracted so the letter page can reuse it. pdfmake mutates nodes during
 * layout, so a fresh object is built on every call — the same object must never be
 * pushed into `content` twice.
 */
function buildHeaderBlock(
  data: OrderPdfData,
  logoDataUrl: string,
  facilityLogoDataUrl: string | undefined,
): Content {
  const leftHeader: Column = {
    columns: [
      { image: logoDataUrl, width: 60, margin: [0, 0, 15, 0] as [number, number, number, number] },
      {
        stack: [buildProviderHeaderStack(data)],
        margin: [8, 0, 0, 0] as [number, number, number, number],
      },
    ],
    width: '*' as const,
  }

  if (data.facilityHeader) {
    const rightColumns: Content[] = []
    if (facilityLogoDataUrl) {
      rightColumns.push({
        image: facilityLogoDataUrl,
        width: 50,
        margin: [0, 0, 10, 0] as [number, number, number, number],
      })
    }
    rightColumns.push({
      stack: [buildFacilityHeaderStack(data.facilityHeader)],
      margin: [0, 0, 0, 0] as [number, number, number, number],
    })

    const rightHeader: Column = {
      columns: rightColumns,
      width: 'auto' as const,
      alignment: 'right',
    }

    return {
      columns: [leftHeader, rightHeader],
      margin: [0, 0, 0, 6] as [number, number, number, number],
    }
  }

  return {
    columns: (leftHeader as { columns: Content[] }).columns,
    margin: [0, 0, 0, 6] as [number, number, number, number],
  }
}

type Margin = [number, number, number, number]

/** "Referral letter, clinical summary and referral requisition". */
function listEnclosed(items: string[]): string {
  const text =
    items.length > 1
      ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
      : items.join('')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** PD-1651 — the fax cover sheet: page 1 of a faxed referral. No link — the fax itself is the delivery. */
export function buildCoverSheetPage(cover: OrderPdfCoverSheet, logoDataUrl: string): Content[] {
  const row = (label: string, value: string) => [
    { text: label, bold: true, color: '#555555' },
    { text: value || '—' },
  ]
  return [
    {
      columns: [
        logoDataUrl ? { image: logoDataUrl, width: 60 } : { text: '' },
        { text: 'FAX', fontSize: 28, bold: true, alignment: 'right', color: '#103F6B' },
      ],
    },
    { text: 'Referral', fontSize: 14, bold: true, margin: [0, 18, 0, 10] as Margin },
    {
      fontSize: 10,
      layout: 'lightHorizontalLines',
      table: {
        widths: [90, '*'],
        body: [
          row('To', [cover.to, cover.toPractice].filter(Boolean).join(' — ')),
          row('Fax', cover.toFax),
          row('From', [cover.fromName, cover.fromOrganization].filter(Boolean).join(' — ')),
          row('Phone', cover.fromPhone),
          row('Reply fax', cover.fromFax),
          row('Date', cover.date),
          row(
            'Re',
            `Referral for ${cover.patientName}${cover.patientDob ? ` (DOB ${cover.patientDob})` : ''}`,
          ),
          row('Enclosed', listEnclosed(cover.enclosed)),
        ],
      },
    },
    {
      text:
        'CONFIDENTIAL: this fax contains protected health information intended only for the named recipient. ' +
        'If you received it in error, notify the sender at the number above and destroy all copies.',
      fontSize: 8,
      color: '#666666',
      margin: [0, 24, 0, 0] as Margin,
    },
  ]
}

/** PD-1651 — the referral letter page: reuses the letterhead, then the letter body and signature. */
export function buildLetterPage(
  letter: OrderPdfLetter,
  header: Content,
  signatureDataUrl: string,
): Content[] {
  return [
    header,
    { text: letter.date, fontSize: 10, margin: [0, 18, 0, 12] as Margin },
    ...letter.body
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p) => ({ text: p, fontSize: 10, lineHeight: 1.3, margin: [0, 0, 0, 8] as Margin })),
    { text: 'Sincerely,', fontSize: 10, margin: [0, 10, 0, 4] as Margin },
    ...(signatureDataUrl
      ? [{ image: signatureDataUrl, width: 120, margin: [0, 0, 0, 2] as Margin }]
      : []),
    { text: letter.closingName, bold: true, fontSize: 10 },
    ...(letter.closingDetail
      ? [{ text: letter.closingDetail, fontSize: 9, color: '#555555' }]
      : []),
  ]
}

/**
 * Whether the requisition has any insurance worth printing.
 *
 * PD-1529 — the heading used to be emitted unconditionally, so an order billed
 * to the patient (or for a patient with no coverage on file) printed a bare
 * "INSURANCE INFORMATION" heading with nothing beneath it.
 */
export function hasInsuranceToPrint(insurance: OrderPdfData['insurance']): boolean {
  return [
    insurance.primaryCarrier,
    insurance.primaryMemberId,
    insurance.primaryGroup,
    insurance.policyholderName,
    insurance.relationship,
  ].some((value) => value?.trim())
}

function buildInsuranceTable(data: OrderPdfData): Content {
  const ins = data.insurance
  const entries: [string, string][] = [
    ['Primary Carrier', ins.primaryCarrier],
    ['Member ID', ins.primaryMemberId],
    ['Group', ins.primaryGroup],
    ['Policyholder', ins.policyholderName],
    ['Relationship', ins.relationship],
  ]

  return buildKeyValueTable(Object.fromEntries(entries.filter(([, v]) => v)))
}

export function buildTestsTable(tests: OrderPdfData['selectedTests']): Content[] {
  if (tests.length === 0) return [{ text: '' }]

  // Detect optional columns
  const hasCodes = tests.some((t) => t.code)
  const hasCategories = tests.some((t) => t.category)
  const hasSpecimen = tests.some((t) => t.specimen)

  const headers = hasCodes ? ['Code', 'Test Name'] : ['Test Name']
  if (hasSpecimen) headers.push('Specimen')
  if (hasCategories) headers.push('Category')

  const rows = tests.map((t) => {
    const row = hasCodes ? [t.code || '', t.name] : [t.name]
    if (hasSpecimen) row.push(t.specimen || '')
    if (hasCategories) row.push(t.category || '')
    return row
  })

  const tableContent: Content[] = [buildDataTable(headers, rows)]

  // Render gene lists as sub-text below the table
  const testsWithGenes = tests.filter((t) => t.geneList)
  if (testsWithGenes.length > 0) {
    for (const t of testsWithGenes) {
      tableContent.push({
        text: [
          { text: `${t.name}: `, fontSize: 7, bold: true, color: TEXT_SECONDARY },
          { text: t.geneList!, fontSize: 7 },
        ],
        margin: [0, 1, 0, 1] as [number, number, number, number],
      })
    }
  }

  return tableContent
}

/**
 * Ordered tests grouped into a section per category.
 *
 * PD-1529 — the flat table repeated the category on every row and stranded each
 * panel's contents in a caption under the whole table, so a panel, a composite
 * test and a plain test all looked alike. Here the category becomes a heading,
 * and a test's contents sit in its own cell, directly under its name.
 */
export function buildGroupedTestsTable(tests: OrderPdfData['selectedTests']): Content[] {
  if (tests.length === 0) return []

  const groups = new Map<string, OrderPdfData['selectedTests']>()
  for (const test of tests) {
    const category = test.category || 'Tests'
    const group = groups.get(category)
    if (group) group.push(test)
    else groups.set(category, [test])
  }

  const content: Content[] = []
  // Sections run in the order their tests arrive. The caller knows which groups
  // matter most on its vendor's form, so sorting here would override that.
  for (const [category, groupTests] of groups) {
    const hasSpecimen = groupTests.some((t) => t.specimen)

    content.push({
      text: category.toUpperCase(),
      fontSize: 6.5,
      bold: true,
      color: TEXT_SECONDARY,
      margin: [0, 4, 0, 2] as [number, number, number, number],
    })

    content.push({
      table: {
        widths: hasSpecimen ? ['*', 70] : ['*'],
        body: groupTests.map((test) => {
          const lines: Content[] = [{ text: test.name, fontSize: 7, bold: true }]
          // One per line, as GTI's form prints them: a comma-joined run is
          // denser and much harder to read down.
          for (const [index, component] of (test.components ?? []).entries()) {
            lines.push({
              text: component,
              fontSize: 6.5,
              color: TEXT_SECONDARY,
              margin: [0, index === 0 ? 2 : 1, 0, 0] as [number, number, number, number],
            })
          }
          const nameCell = { stack: lines }
          return hasSpecimen ? [nameCell, { text: test.specimen || '', fontSize: 7 }] : [nameCell]
        }),
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => BORDER_COLOR,
        vLineColor: () => BORDER_COLOR,
        paddingTop: () => 2,
        paddingBottom: () => 2,
        paddingLeft: () => 3,
        paddingRight: () => 3,
      },
      margin: [0, 0, 0, 2] as [number, number, number, number],
    })
  }

  return content
}

function buildSignatureBlock(data: OrderPdfData): Content[] {
  const content: Content[] = []

  if (data.signatureDataUrl) {
    // Use a 3-row table so the underlines and labels share the exact same Y coordinate.
    // Row 1: signature image (left) + date text (right) — variable height content
    // Row 2: underlines — guaranteed aligned
    // Row 3: labels — guaranteed aligned
    content.push({
      margin: [0, 10, 0, 0] as [number, number, number, number],
      table: {
        widths: [180, 20, 120],
        body: [
          // Row 1: content
          [
            { image: data.signatureDataUrl, width: 140, height: 35 },
            { text: '' },
            {
              text: data.signatureDate,
              fontSize: 8,
              margin: [0, 23, 0, 0] as [number, number, number, number],
            },
          ],
          // Row 2: underlines
          [
            { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 170, y2: 0, lineWidth: 0.5 }] },
            { text: '' },
            { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 110, y2: 0, lineWidth: 0.5 }] },
          ],
          // Row 3: labels
          [
            { text: 'Ordering Provider Signature', fontSize: 6, color: '#666' },
            { text: '' },
            { text: 'Date', fontSize: 6, color: '#666' },
          ],
        ],
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        paddingTop: () => 1,
        paddingBottom: () => 1,
        paddingLeft: () => 0,
        paddingRight: () => 0,
      },
    })
  }

  return content
}

/** Generates a BeHuman-branded lab order PDF and returns the bytes. */
export async function generateBeHumanOrderPdf(data: OrderPdfData): Promise<Uint8Array> {
  let logoDataUrl = ''
  try {
    logoDataUrl = await urlToDataURL(logo)
  } catch (err) {
    logError('BeHuman logo fetch', err)
  }

  // Facility logo is pre-fetched as a data URL by the caller (via medplum.download,
  // which handles auth + token refresh). The generator just embeds it.
  const facilityLogoDataUrl = data.facilityHeader?.logoDataUrl

  const content: Content[] = []

  // Header: logo + provider info (letterhead style), optionally with facility on the right
  content.push(buildHeaderBlock(data, logoDataUrl, facilityLogoDataUrl))

  // Title block
  content.push({
    text: data.orderTitle || 'Lab Order Requisition',
    fontSize: 13,
    bold: true,
    color: BRAND_BLUE,
    alignment: 'left',
    margin: [0, 4, 0, 1],
  })
  const subtitle = data.subtitle ?? `${data.vendorName} \u2014 ${data.orderTitle}`
  if (subtitle) {
    content.push({
      text: subtitle,
      fontSize: 8,
      color: '#666',
      alignment: 'left',
      margin: [0, 0, 0, 1],
    })
  }
  content.push({
    text: `Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
    fontSize: 7,
    color: '#999',
    alignment: 'left',
    margin: [0, 0, 0, 4],
  })

  // PD-1312 — patient-facing instruction. Sits above the divider because it
  // tells the PATIENT what to do with the printout, unlike everything below,
  // which is addressed to the lab.
  if (data.patientInstruction) {
    content.push({
      text: data.patientInstruction,
      fontSize: 8,
      bold: true,
      color: BRAND_BLUE,
      alignment: 'left',
      margin: [0, 0, 0, 4] as [number, number, number, number],
    })
  }

  // Divider
  content.push({
    canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: BRAND_BLUE }],
    margin: [0, 0, 0, 4],
  })

  // Patient Information
  content.push(buildSectionTitle('Patient Information'))
  content.push(buildPatientInfoTable(data))

  // Insurance — omitted entirely when there is nothing to show.
  if (hasInsuranceToPrint(data.insurance)) {
    content.push(buildSectionTitle('Insurance Information'))
    content.push(buildInsuranceTable(data))
  }

  // Sections that render before diagnosis (e.g. referral reason)
  if (data.preDiagnosisSections) {
    for (const section of data.preDiagnosisSections) {
      content.push(...renderVendorSection(section))
    }
  }

  // Diagnosis / ICD-10
  if (data.icd10Codes.length > 0 || data.diagnosisText) {
    content.push(buildSectionTitle('Diagnosis / ICD-10 Codes'))
    if (data.icd10Codes.length > 0) {
      content.push(buildList(data.icd10Codes))
    }
    if (data.diagnosisText) {
      content.push({
        text: data.diagnosisText,
        fontSize: 7,
        margin: [0, 0, 0, 2] as [number, number, number, number],
      })
    }
  }

  // Selected Tests
  if (data.selectedTests.length > 0) {
    content.push(buildSectionTitle('Ordered Tests'))
    content.push(
      ...(data.groupTestsByCategory
        ? buildGroupedTestsTable(data.selectedTests)
        : buildTestsTable(data.selectedTests)),
    )
  }

  // Vendor-specific sections
  for (const section of data.vendorSections) {
    content.push(...renderVendorSection(section))
  }

  // PD-1312 — where results go when nothing was faxed to a facility. Boxed so
  // the draw site can't miss it; the practice fax is the only return path.
  if (data.resultRouting) {
    content.push({
      table: {
        widths: ['*'],
        body: [
          [
            {
              stack: [
                {
                  text: 'RESULT ROUTING',
                  fontSize: 8,
                  bold: true,
                  color: BRAND_BLUE,
                  margin: [0, 0, 0, 2] as [number, number, number, number],
                },
                { text: data.resultRouting, fontSize: 7.5, color: '#333' },
              ],
              margin: [6, 5, 6, 5] as [number, number, number, number],
            },
          ],
        ],
      },
      layout: {
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => BRAND_BLUE,
        vLineColor: () => BRAND_BLUE,
        fillColor: () => '#F4F8FB',
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
      },
      margin: [0, 4, 0, 4] as [number, number, number, number],
    })
  }

  // Signature
  content.push(...buildSignatureBlock(data))

  const copyrightText =
    'Copyright \u00A9 2026 beHuman Corp. The contents of this document and/or fax transmission are intended only for the use of the individual or entity to which they are addressed and may contain information that is legally privileged, confidential, and exempt from disclosure, including protected health information. If you are not the intended recipient, you are hereby notified that any dissemination, distribution, or copying of this document is strictly prohibited. If you have received or obtained this document or fax transmission in error, please notify the original sender or contact beHuman Corp. by email at privacy@behuman.health and delete this document immediately.'

  // PD-1651 — faxed referrals lead with a cover sheet, then the referral letter,
  // before the requisition pages built above.
  const leadPages: Content[] = []
  if (data.coverSheet) {
    leadPages.push(...buildCoverSheetPage(data.coverSheet, logoDataUrl), {
      text: '',
      pageBreak: 'after',
    })
  }
  if (data.letter) {
    leadPages.push(
      ...buildLetterPage(
        data.letter,
        buildHeaderBlock(data, logoDataUrl, facilityLogoDataUrl),
        data.signatureDataUrl,
      ),
      { text: '', pageBreak: 'after' },
    )
  }

  const docDefinition: TDocumentDefinitions = {
    pageSize: 'LETTER',
    pageMargins: [36, 30, 36, 65],
    content: [...leadPages, ...content],
    footer: (currentPage, pageCount) => ({
      stack: [
        {
          columns: [
            {
              text: `BeHuman Medical Group PA \u2014 ${data.vendorName} Order`,
              fontSize: 6,
              color: '#999',
              margin: [36, 0, 0, 0],
            },
            {
              text: `Page ${currentPage} of ${pageCount}`,
              fontSize: 6,
              color: '#999',
              alignment: 'right',
              margin: [0, 0, 36, 0],
            },
          ],
        },
        {
          text: copyrightText,
          fontSize: 4.5,
          color: '#aaa',
          margin: [36, 3, 36, 0],
          lineHeight: 1.1,
        },
      ],
    }),
    defaultStyle: {
      fontSize: 8,
    },
  }

  return new Promise<Uint8Array>((resolve, reject) => {
    const timeoutId = setTimeout(() => reject(new Error('PDF generation timed out')), 30_000)
    try {
      pdfMake.createPdf(docDefinition).getBuffer((buffer) => {
        clearTimeout(timeoutId)
        resolve(new Uint8Array(buffer))
      })
    } catch (err) {
      clearTimeout(timeoutId)
      reject(err instanceof Error ? err : new Error(String(err)))
    }
  })
}
